import { Camera, MapPin, Shield, Wrench, HelpCircle } from 'lucide-react';
import { cn } from '../../../utils/cn';
import QuestionnaireBuilder from '../QuestionnaireBuilder';

// Clock-in radius options (in metres)
const CLOCK_IN_RADIUS_OPTIONS = [
  { value: null, label: 'Not Required', description: 'No location verification' },
  { value: 20, label: 'Within 20 metres', description: 'Very precise' },
  { value: 50, label: 'Within 50 metres', description: 'Recommended' },
  { value: 100, label: 'Within 100 metres', description: 'More flexible' }
];

export default function RequirementsTab({
  formData,
  onChange,
  errors
}) {
  return (
    <div className="space-y-6">
      {/* Photos Required */}
      <div className="p-4 bg-surface rounded-lg border border-border">
        <h3 className="text-sm font-medium text-text-primary mb-3 flex items-center gap-2">
          <Camera className="w-4 h-4 text-primary-orange" />
          Photos Required
        </h3>

        <div className="flex gap-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData?.photosRequiredBefore !== false}
              onChange={(e) => onChange?.({ photosRequiredBefore: e?.target?.checked })}
              className="w-4 h-4 rounded border-gray-600 bg-white text-primary-orange focus:ring-primary-orange focus:ring-offset-background"
            />
            <span className="text-sm text-white-shade-1">Before photos</span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData?.photosRequiredAfter !== false}
              onChange={(e) => onChange?.({ photosRequiredAfter: e?.target?.checked })}
              className="w-4 h-4 rounded border-gray-600 bg-white text-primary-orange focus:ring-primary-orange focus:ring-offset-background"
            />
            <span className="text-sm text-white-shade-1">After photos</span>
          </label>
        </div>
      </div>

      {/* Clock-in Requirements */}
      <div className="p-4 bg-surface rounded-lg border border-border">
        <h3 className="text-sm font-medium text-text-primary mb-3 flex items-center gap-2">
          <MapPin className="w-4 h-4 text-primary-orange" />
          Clock-in Requirements
        </h3>

        {/* Radius Selection */}
        <div className="space-y-2 mb-4">
          {CLOCK_IN_RADIUS_OPTIONS?.map((option) => (
            <label
              key={option?.value ?? 'none'}
              className={cn(
                'flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
                formData?.clockInRadius === option?.value
                  ? 'border-accent'
                  : 'border-border'
              )}
            >
              <input
                type="radio"
                name="clockInRadius"
                checked={formData?.clockInRadius === option?.value}
                onChange={() => onChange?.({ clockInRadius: option?.value })}
                className="sr-only"
              />
              <div className={cn(
                'w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0',
                formData?.clockInRadius === option?.value ? 'border-accent' : 'border-charcoal-shade-2'
              )}>
                {formData?.clockInRadius === option?.value && (
                  <div className="w-2 h-2 rounded-full bg-accent" />
                )}
              </div>
              <div>
                <p className={cn('text-sm', formData?.clockInRadius === option?.value ? 'text-accent' : 'text-text-primary')}>{option?.label}</p>
                <p className="text-xs text-white-shade-2">{option?.description}</p>
              </div>
            </label>
          ))}
        </div>

        {/* Flexibility Option */}
        <label className="flex items-start gap-3 p-3 rounded-lg border border-border bg-background cursor-pointer">
          <input
            type="checkbox"
            checked={formData?.clockInFlexibility || false}
            onChange={(e) => onChange?.({ clockInFlexibility: e?.target?.checked })}
            className="w-4 h-4 mt-0.5 rounded border-gray-600 bg-white text-primary-orange focus:ring-primary-orange focus:ring-offset-background"
          />
          <div>
            <p className="text-sm text-text-primary">Allow late clock-in submission</p>
            <p className="text-xs text-white-shade-2">
              For locations with poor internet connectivity, allow contractor to submit clock-in data later
            </p>
          </div>
        </label>

        {/* Late Submission Window (shown when flexibility is enabled) */}
        {formData?.clockInFlexibility && (
          <div className="mt-3 ml-7 p-3 rounded-lg border border-border bg-background">
            <label className="block text-sm text-text-primary mb-2">
              Submission deadline after scheduled date:
            </label>
            <select
              value={formData?.lateSubmissionWindowHours || 24}
              onChange={(e) => onChange?.({ lateSubmissionWindowHours: parseInt(e?.target?.value, 10) })}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-background focus:outline-none focus:border-primary-orange text-sm"
            >
              <option value={24}>24 hours (next day)</option>
              <option value={48}>48 hours (2 days)</option>
              <option value={72}>72 hours (3 days)</option>
              <option value={168}>7 days</option>
            </select>
            <p className="text-xs text-white-shade-2 mt-2">
              Contractor can submit reports until this deadline after the scheduled job date
            </p>
          </div>
        )}
      </div>

      {/* Questionnaire */}
      <div className="p-4 bg-surface rounded-lg border border-border">
        <h3 className="text-sm font-medium text-text-primary mb-3 flex items-center gap-2">
          <HelpCircle className="w-4 h-4 text-primary-orange" />
          Questionnaire
        </h3>
        <p className="text-xs text-white-shade-2 mb-4">
          Add questions for the contractor to answer when submitting their report
        </p>

        <QuestionnaireBuilder
          value={formData?.questionnaireTemplate || []}
          onChange={(questions) => onChange?.({ questionnaireTemplate: questions })}
        />
      </div>

      {/* Safety/PPE Requirements */}
      <div className="p-4 bg-surface rounded-lg border border-border">
        <h3 className="text-sm font-medium text-text-primary mb-3 flex items-center gap-2">
          <Shield className="w-4 h-4 text-primary-orange" />
          Safety / PPE Requirements
        </h3>
        <textarea
          value={formData?.safetyRequirements || ''}
          onChange={(e) => onChange?.({ safetyRequirements: e?.target?.value })}
          placeholder="e.g., Steel-capped boots, high-vis vest, safety glasses, hard hat"
          rows={2}
          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-background placeholder-gray-500 focus:outline-none focus:border-primary-orange resize-none text-sm"
        />
      </div>

      {/* Equipment/Materials List */}
      <div className="p-4 bg-surface rounded-lg border border-border">
        <h3 className="text-sm font-medium text-text-primary mb-3 flex items-center gap-2">
          <Wrench className="w-4 h-4 text-primary-orange" />
          Equipment / Materials List
        </h3>
        <textarea
          value={formData?.equipmentList || ''}
          onChange={(e) => onChange?.({ equipmentList: e?.target?.value })}
          placeholder="e.g., Mop, bucket, floor cleaner, microfiber cloths, vacuum cleaner"
          rows={2}
          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-background placeholder-gray-500 focus:outline-none focus:border-primary-orange resize-none text-sm"
        />
      </div>
    </div>
  );
}
